import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { Alert, Button, PageHeader, Select, TextInput } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { formatCents } from "../../GiftCardProducts/giftCardFormat";
import GiftCardTabs, { TAB_REDEEM } from "../../GiftCardProducts/components/GiftCardTabs";
import { currencySymbol, parseAmountCents } from "../giftCardLabels";
import "../styles/giftCards.css";

const errorsFrom = (error, fallback) => {
  const errors = error?.response?.data?.errors;
  return errors && errors.length > 0 ? errors.join(", ") : fallback;
};

const secondsLeft = (endsAt, now) => Math.max(0, Math.ceil((endsAt - now) / 1000));

const cardKey = (code) => code.replace(/[\s\p{Dash}]/gu, "").toUpperCase();

const countdown = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const worksAtText = (card, teamName) =>
  card.scope === "venues" ? `Works at ${card.venueNames.join(", ")}` : `Works at any ${teamName} venue`;

const GiftCardRedeemForm = ({ lookupPath, redeemPath, cardsPath, productsPath, redeemPagePath, teamName, venues }) => {
  const [code, setCode] = useState("");
  const [card, setCard] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [amount, setAmount] = useState("");
  const [venueId, setVenueId] = useState(venues.length === 1 ? String(venues[0].id) : "");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [redemption, setRedemption] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [replayNotice, setReplayNotice] = useState(null);
  const [recent, setRecent] = useState([]);
  const latestLookup = useRef(0);
  const tokensByCard = useRef({});

  const tokenFor = (cardCode) => {
    const key = cardKey(cardCode);
    if (!tokensByCard.current[key]) tokensByCard.current[key] = uuidv4();
    return tokensByCard.current[key];
  };

  const withinWindow = (entry) => secondsLeft(entry.undoEndsAt, now) > 0;
  const undoable = (entry) => entry.status === "captured" && withinWindow(entry);
  const stillShown = (entry) => withinWindow(entry) && ["captured", "reversed"].includes(entry.status);
  const undoSecondsLeft = redemption ? secondsLeft(redemption.undoEndsAt, now) : 0;
  const canUndo = Boolean(redemption) && undoable(redemption);
  const visibleRecent = recent.filter(stillShown);
  const ticking = canUndo || visibleRecent.length > 0;

  useEffect(() => {
    if (!ticking) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [ticking]);

  const money = (cents, currency) => formatCents(cents, currency || card?.currency || redemption?.currency);
  const amountCents = parseAmountCents(amount);
  const venue = venues.find((option) => String(option.id) === venueId);
  const notValidHere = Boolean(card && venue && !card.usableVenueIds.includes(venue.id));
  const overBalance = Boolean(card && amountCents > card.balanceCents);
  const overSpendable = Boolean(card && !overBalance && amountCents > card.spendableCents);
  const heldCents = card ? card.balanceCents - card.spendableCents : 0;
  const unreadableAmount = amount.trim().length > 0 && amountCents === 0;
  const amountError = (unreadableAmount && "Enter an amount like 18.50") ||
    (overBalance && "That's more than this card's balance") ||
    (overSpendable && `${money(heldCents)} of this card is held by a checkout in progress. You can redeem up to ${money(card.spendableCents)} right now`) ||
    null;
  const ready = Boolean(card) && !card.refusal && amountCents > 0 && !amountError && venue && !notValidHere && note.trim().length > 0 && !submitting;

  const resetCard = () => {
    setCard(null);
    setAmount("");
    setNote("");
    setSubmitError(null);
  };

  const startOver = () => {
    if (undoing) return;
    setRecent((entries) => [redemption, ...entries].filter(stillShown));
    resetCard();
    setCode("");
    setLookupError(null);
    setRedemption(null);
    setReplayNotice(null);
    setUndoError(null);
  };

  const changeCode = (value) => {
    if (submitting) return;
    latestLookup.current += 1;
    setCode(value);
    setLookupError(null);
    setLookingUp(false);
    if (card) resetCard();
  };

  const lookUp = async (event) => {
    event.preventDefault();
    const requestedCode = code.trim();
    if (lookingUp || submitting || requestedCode.length === 0) return;
    latestLookup.current += 1;
    const lookup = latestLookup.current;
    setLookingUp(true);
    setLookupError(null);
    setUndoError(null);
    resetCard();
    try {
      const { data } = await client.post(lookupPath, { gift_card_code: requestedCode });
      if (lookup === latestLookup.current) setCard({ ...data, code: requestedCode });
    } catch (error) {
      if (lookup === latestLookup.current) setLookupError(errorsFrom(error, "Couldn't look up that code. Please try again."));
    } finally {
      if (lookup === latestLookup.current) setLookingUp(false);
    }
  };

  const redeem = async () => {
    if (!ready) return;
    setSubmitting(true);
    setSubmitError(null);
    setUndoError(null);
    try {
      const { data } = await client.post(redeemPath, {
        gift_card_code: card.code,
        venue_ownership_id: venue.id,
        amount_cents: amountCents,
        note,
        idempotency_token: tokenFor(card.code),
      });
      delete tokensByCard.current[cardKey(card.code)];
      const receivedAt = Date.now();
      setNow(receivedAt);
      setReplayNotice(
        data.amountCents === amountCents && data.venueName === venue.name
          ? null
          : `Your earlier attempt already went through: ${money(data.amountCents)} at ${data.venueName}. Your changes weren't applied.`
      );
      setRedemption({ ...data, undoEndsAt: receivedAt + data.undoSecondsLeft * 1000 });
    } catch (error) {
      setSubmitError(errorsFrom(error, "Couldn't redeem this gift card. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const undo = async (entry, onUndone) => {
    if (undoing) return;
    setUndoing(true);
    setUndoError(null);
    try {
      const { data } = await client.post(entry.undoPath);
      onUndone({ ...entry, ...data });
    } catch (error) {
      setUndoError(errorsFrom(error, "Couldn't undo this redemption. Please try again."));
    } finally {
      setUndoing(false);
    }
  };

  const undoRecent = (entry) =>
    undo(entry, (updated) => setRecent((entries) => entries.map((item) => (item.id === updated.id ? updated : item))));

  const recentList = visibleRecent.length > 0 && (
    <div className="gift-card-redeem-recent" data-testid="redeem-recent">
      <div className="gift-card-redeem-muted">Recent redemptions</div>
      {visibleRecent.map((entry) => (
        <div className="gift-card-redeem-recent-row" key={entry.id}>
          <span>{`•••• ${entry.last4} · ${money(entry.amountCents, entry.currency)}`}</span>
          {entry.status === "reversed" ? (
            <span className="gift-card-redeem-muted">Undone</span>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => undoRecent(entry)} disabled={undoing}>
              {`Undo (${countdown(secondsLeft(entry.undoEndsAt, now))})`}
            </Button>
          )}
        </div>
      ))}
    </div>
  );

  const header = (
    <PageHeader title="Redeem a gift card" supportingText="For things sold outside Opendate, like a bar tab or merch." />
  );
  const tabs = (
    <GiftCardTabs current={TAB_REDEEM} productsPath={productsPath} cardsPath={cardsPath} redeemPath={redeemPagePath} />
  );

  if (venues.length === 0) {
    return (
      <div className="gift-card-redeem">
        {header}
        {tabs}
        <div className="gift-card-redeem-panel">
          <Alert color="warning" title="You don't have access to any venues" description="Ask a team admin to give you access to the venue where you're taking gift cards." />
        </div>
      </div>
    );
  }

  if (redemption) {
    const undone = redemption.status === "reversed";
    return (
      <div className="gift-card-redeem">
        {header}
        {tabs}
        <div className="gift-card-redeem-panel" data-testid="redeem-result">
          <Alert
            color={undone ? "gray" : "success"}
            title={undone ? "Redemption undone" : `Redeemed ${money(redemption.amountCents)}`}
            description={
              undone
                ? `${money(redemption.amountCents)} is back on gift card •••• ${redemption.last4}.`
                : `Taken off gift card •••• ${redemption.last4}.`
            }
          />
          {replayNotice && <Alert color="warning" title={replayNotice} />}
          <div className="gift-card-modal-preview">
            <span>Left on card</span>
            <strong>{money(redemption.balanceCents)}</strong>
          </div>
          {undoError && <Alert color="error" title={undoError} />}
          <div className="gift-card-redeem-actions">
            {canUndo && (
              <Button variant="secondary" onClick={() => undo(redemption, setRedemption)} disabled={undoing}>
                {`Undo (${countdown(undoSecondsLeft)})`}
              </Button>
            )}
            <Button variant="primary" onClick={startOver} disabled={undoing}>
              Redeem another card
            </Button>
          </div>
        </div>
        {recentList}
      </div>
    );
  }

  return (
    <div className="gift-card-redeem">
      {header}
      {tabs}
      <div className="gift-card-redeem-panel">
        <form className="gift-card-redeem-lookup" onSubmit={lookUp}>
          <div className="gift-card-redeem-lookup-input">
            <TextInput
              label="Gift card code"
              placeholder="Type or scan the code"
              autoComplete="off"
              autoFocus
              value={code}
              disabled={submitting}
              onChange={(event) => changeCode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.nativeEvent.isComposing) lookUp(event);
              }}
              error={lookupError}
            />
          </div>
          <Button type="submit" variant="secondary" disabled={lookingUp || code.trim().length === 0}>
            Look up
          </Button>
        </form>

        {card && (
          <>
            <div className="gift-card-redeem-card" data-testid="redeem-card">
              <div>
                <strong>{`Gift card •••• ${card.last4}`}</strong>
                <div className="gift-card-redeem-muted">{worksAtText(card, teamName)}</div>
              </div>
              <div className="gift-card-redeem-balance">
                <span className="gift-card-redeem-muted">Balance</span>
                <strong>{money(card.balanceCents)}</strong>
              </div>
            </div>

            {card.refusal ? (
              <Alert color="error" title={card.refusal} />
            ) : (
              <>
                <div className="gift-card-redeem-fields">
                  <TextInput
                    label="Amount"
                    inputMode="decimal"
                    leadingText={currencySymbol(card.currency)}
                    placeholder="0.00"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    error={amountError}
                  />
                  <Select
                    label="Venue"
                    placeholder="Choose a venue"
                    options={venues.map((option) => ({ value: String(option.id), label: option.name }))}
                    value={venueId}
                    onChange={(value) => setVenueId(value || "")}
                  />
                </div>
                {notValidHere && <Alert color="error" title={`This gift card can't be used at ${venue.name}`} />}
                <TextInput
                  label="Note (required)"
                  placeholder="What was it for? e.g. Bar tab"
                  maxLength={1000}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
                <div className="gift-card-modal-preview" data-testid="redeem-preview">
                  <span>Left on card after</span>
                  <strong>{amountError ? "—" : money(card.balanceCents - amountCents)}</strong>
                </div>
                {submitError && <Alert color="error" title={submitError} />}
                <Button variant="primary" onClick={redeem} disabled={!ready}>
                  {`Redeem ${money(amountCents)}`}
                </Button>
              </>
            )}
          </>
        )}
      </div>
      {recentList}
      {visibleRecent.length > 0 && undoError && <Alert color="error" title={undoError} />}
    </div>
  );
};

GiftCardRedeemForm.propTypes = {
  lookupPath: PropTypes.string.isRequired,
  redeemPath: PropTypes.string.isRequired,
  cardsPath: PropTypes.string,
  productsPath: PropTypes.string,
  redeemPagePath: PropTypes.string.isRequired,
  teamName: PropTypes.string.isRequired,
  venues: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string.isRequired }))
    .isRequired,
};

GiftCardRedeemForm.defaultProps = {
  cardsPath: null,
  productsPath: null,
};

export default GiftCardRedeemForm;
